// buyer information → customer picker for the configurator. search + select an existing
// customer (auto-populates the buyer fields), or create a new one. Selecting/creating links
// the order to a customer master record; the committed revision still snapshots the buyer text.

import { useEffect, useId, useState } from 'react';
import { customerDisplayName, type Customer, type CustomerFieldsInput } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useCustomer, useCustomerSearch } from '../features/customers.js';
import { CustomerFormModal } from './CustomerFormModal.js';
import { InlineError } from './ui.js';

export function CustomerPicker({
  linkedId,
  linkedName,
  onSelect,
  onClear,
  newDefaults,
  disabled,
}: {
  linkedId: number | null;
  linkedName?: string;
  onSelect: (c: Customer) => void;
  onClear: () => void;
  newDefaults?: Partial<CustomerFieldsInput>;
  disabled?: boolean;
}) {
  const { can } = useAuth();
  const hintId = useId();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const search = useCustomerSearch(open ? term : '');
  const created = useCustomer(pendingId);

  // after creating a customer we only get its id back — fetch it, then link + populate.
  useEffect(() => {
    if (pendingId && created.data) {
      onSelect(created.data);
      setPendingId(null);
      setTerm('');
      setOpen(false);
    }
  }, [pendingId, created.data, onSelect]);

  const pick = (c: Customer) => { onSelect(c); setTerm(''); setOpen(false); };

  if (linkedId && !open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="pill ok">Linked customer</span>
        <span style={{ fontWeight: 600 }}>{linkedName || `Customer #${linkedId}`}</span>
        {!disabled && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(true)}>Change</button>}
        {!disabled && <button type="button" className="btn btn-sm btn-ghost" onClick={onClear}>Unlink</button>}
      </div>
    );
  }

  return (
    <div>
      <div className="cust-picker-row">
        <input
          className="field"
          placeholder="Search customers"
          aria-label="Search existing customer"
          aria-describedby={hintId}
          value={term}
          disabled={disabled}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        <div className="muted cust-picker-hint" id={hintId}>By name, company, VAT, email or phone</div>
        {can('customer_database.create') && !disabled && (
          <button type="button" className="btn btn-sm" onClick={() => setNewOpen(true)}>+ New customer</button>
        )}
        {linkedId && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>Cancel</button>}
      </div>

      {open && term.trim().length >= 2 && (
        <div className="cfg-cust-results">
          {search.isLoading && <div className="muted" style={{ padding: 8 }}>Searching…</div>}
          {search.isError && <InlineError error={search.error} />}
          {search.data?.customers.length === 0 && <div className="muted" style={{ padding: 8 }}>No matching customers.</div>}
          {search.data?.customers.map((c) => (
            <button type="button" key={c.id} className="cfg-cust-row" onClick={() => pick(c)}>
              <span style={{ fontWeight: 600 }}>{customerDisplayName(c)}</span>
              <span className="muted cust-picker-meta">
                {[c.vatNumber, c.email, c.telephone || c.mobile, c.country].filter(Boolean).join(' · ') || '-'}
              </span>
            </button>
          ))}
        </div>
      )}

      {newOpen && (
        <CustomerFormModal initial={newDefaults} onClose={() => setNewOpen(false)} onSaved={(id) => setPendingId(id)} />
      )}
    </div>
  );
}
